/**
 * @fileoverview AI chat API transport for the system-web AI chatbot.
 * @module system-web/services/ai-chat-api-client
 *
 * Responsibilities:
 * - Provide the HTTP transport layer that the shared @firesquirrel/ai-chatbot
 *   service uses to reach the AI orchestration endpoints.
 * - Handle FormData uploads (e.g. PDF analysis) separately from JSON requests.
 * - Attach bearer tokens for authenticated system API calls.
 *
 * Dependencies:
 * - ../apiClient for authenticated JSON requests and token retrieval.
 *
 * Architecture:
 * - This is a service-level wrapper around the online AI API; it is consumed
 *   by App.tsx only to inject transport into the shared aiChatService.
 * - Pages and components must not import ../apiClient directly.
 *
 * Notes:
 * - This file intentionally does not implement local-first persistence;
 *   conversation history and context assembly remain the responsibility of
 *   the shared ai-chatbot package.
 */

import { apiFetch, getToken } from '../apiClient';

/**
 * POST helper for AI chat requests.
 * FormData bodies are sent with a manual fetch so the browser sets the
 * multipart boundary; JSON bodies use the standard apiFetch wrapper.
 *
 * @errorHandling
 * Strategy: Surface HTTP failures as thrown errors so the ai-chatbot package
 * can display an error message. Non-JSON error bodies fall back to the status.
 * User Impact: Chat message shows an error instead of hanging silently.
 * Logging: Console is not used here; callers log as needed.
 */
export async function aiChatPost<T>(url: string, data: unknown): Promise<T> {
  // Handle FormData separately (for file uploads)
  if (data instanceof FormData) {
    const headers: Record<string, string> = {};
    const token = await getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: data,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error((body as { error?: string }).error || `Request failed: ${res.status}`);
    }

    return res.json() as T;
  }

  // Regular JSON requests
  return apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(data),
  }) as T;
}

/**
 * GET helper for AI chat schema/context endpoints.
 */
export async function aiChatGet<T>(url: string): Promise<T> {
  return apiFetch(url) as T;
}
